import { memo, useMemo } from 'react';

import { Elements } from '@stripe/react-stripe-js';
import { type StripeElementsOptions } from '@stripe/stripe-js';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { getEnvVars } from '@order/utils';

import { useStripeLoadMonitor } from '../../hooks';
import { type StripeElementsProviderProps } from '../../types';
import { getMinimumAmount, loadStripeWithRetry } from '../../utils';

export const StripeElementsProvider = memo((props: StripeElementsProviderProps) => {
  const { children, source, amount = 0, sessionId, onFailedToLoadStripe } = props;

  const stripePaymentMethodConfig = useFeatureFlag('permanent-stripe-payment-method-config'); // prettier-ignore
  const paymentMethodConfiguration = stripePaymentMethodConfig?.defaultId;

  const stripePromise = useMemo(() => loadStripeWithRetry(publicKey, source), [source]);
  useStripeLoadMonitor(stripePromise, onFailedToLoadStripe);

  const elementsStripe = useMemo(() => stripePromise.catch(() => null), [stripePromise]);

  const options = useMemo<StripeElementsOptions>(
    () => ({
      amount: getMinimumAmount(amount),
      currency,
      mode,
      captureMethod,
      customerSessionClientSecret: sessionId,
      paymentMethodConfiguration,
    }),
    [amount, sessionId, paymentMethodConfiguration],
  );

  return (
    <Elements stripe={elementsStripe} options={options}>
      {children}
    </Elements>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const currency = 'usd';
const mode = 'payment';
const captureMethod = 'manual';
const publicKey = getEnvVars().STRIPE_PUBLIC_KEY;
